import type { TFunction } from 'react-i18next';

import { type StepDisplayConfig } from 'pages/AutomationEngine/steps';
import {
  type GenericOption,
  type Step,
  type StepMenuOptions,
  StepMenuOptionsValues,
} from 'pages/AutomationEngine/types';
import {
  isBranchStep,
  isLoopStepNext,
} from 'pages/AutomationEngine/store/ts-utilities';

// Pulled out of useExecutionHistory so the rules can be exercised directly:
// importing the hook pulls in the router, which needs a DOM.
export const getStepMenuOption = (
  step: Step,
  stepConfig: StepDisplayConfig,
  grandChildrenCount: number,
  stepIdPreview: string | undefined | null,
  branches: Record<string, boolean | 'yes' | 'no'>,
  inLoop: boolean,
  t: TFunction
) => {
  // The loop's Next/Finish card suppresses every action - it is edited, moved
  // and deleted through the Loop card that opens it - so it offers no menu at
  // all rather than a menu that is empty or skip-only.
  if (isLoopStepNext(step)) {
    return [];
  }

  const noPath = step.type === 'goal' ? t('Unmet Path') : t('No Path');
  const yesPath = step.type === 'goal' ? t('Met Path') : t('Yes Path');
  const options: Array<GenericOption<StepMenuOptions>> = [];

  if (stepConfig.creatable) {
    options.push({ label: t('Edit'), value: StepMenuOptionsValues.edit });
  }

  if (
    stepConfig.deletable &&
    (!isBranchStep(step) || grandChildrenCount <= 1)
  ) {
    options.push({ label: t('Delete'), value: StepMenuOptionsValues.delete });
  }

  if (
    isBranchStep(step) &&
    grandChildrenCount > 0 &&
    !(`${step.id}.merge-split` in branches)
  ) {
    options.push({
      label: t('Add Branch'),
      value: StepMenuOptionsValues.add_branch,
    });
  }

  if (
    step.type !== 'initialize_variable' &&
    stepConfig.type !== 'unknown' &&
    stepConfig.type !== 'branch'
  ) {
    if (step.shouldSkipExecution) {
      options.push({
        label: t('Un-skip'),
        value: StepMenuOptionsValues.unskip,
      });

      if (step.type === 'goal' || step.type === 'condition') {
        options.push({
          label: t('Skip to {{condition}}', {
            condition: step.continueWithBranch === 'yes' ? noPath : yesPath,
          }),
          value: StepMenuOptionsValues.continue_with_branch,
        });
      }
    } else {
      options.push({ label: t('Skip'), value: 'skip' });
    }
  }

  // Duplicating inside a loop would place the copy in a branch, which a loop
  // does not allow, so the option is dropped rather than offered and refused.
  if (stepConfig.duplicatable && !inLoop) {
    options.push({
      label: t('Duplicate'),
      value: StepMenuOptionsValues.duplicate_step,
    });
  }

  if (stepConfig.copyable) {
    options.push({
      label: t('Copy'),
      value: StepMenuOptionsValues.copy_step,
    });
  }

  // Nothing in a loop can be merged or unmerged. Every path has to converge on
  // the Next/Finish, which is why a goal or condition created in a loop is a
  // branch group from the start and an unmerged one may not be moved into one -
  // so neither option has anything to act on here.
  if (stepConfig.mergeable && !inLoop) {
    options.push(
      step.isBranchGroupInitiator === false
        ? {
            label: t('Merge Branches'),
            value: StepMenuOptionsValues.merge_branches,
          }
        : {
            label: t('Unmerge Branches'),
            value: StepMenuOptionsValues.unmerge_branches,
          }
    );
  }

  if (stepIdPreview) {
    options.push({
      label: t('ID {{ stepIdPreview }} (copy)', { stepIdPreview }),
      value: StepMenuOptionsValues.copy_step_id,
    });
  }

  return options;
};
